import { hasE1rm, loadTrackKey } from "./e1rmPoints.js";
import type { TimedSessionResult } from "./progressTypes.js";
import type { TimeRange } from "./timeRange.js";

// #389: the session maths of the exercise tracker (exerciseTracker.ts keeps
// the athlete's choice). Next to e1rmPoints.ts because it keys the rows the
// same way: one load track per (exercise, machine), EQ-1-HIST. Two machines'
// kilos are not comparable, so a chart reads ONE track of an exercise.

/** The statistics on offer, each read off a session's exercise result. */
export const TRACKER_STATS = ["e1rm", "topWeight", "volume", "topReps"] as const;
export type TrackerStat = (typeof TRACKER_STATS)[number];

/** One session of one exercise: the value of each statistic it carries. */
export type TrackerSession = { atMs: number; values: Partial<Record<TrackerStat, number>> };

/** The track a chart reads, and whether the exercise had others in range
 *  (then the chart names the machine). */
export type TrackerTrack = {
  /** Absent: the Default machine. */
  equipmentInstance?: string;
  /** Oldest first. */
  sessions: TrackerSession[];
  /** How many tracks of the exercise carry a value in the range. */
  trackCount: number;
};

type ExerciseRow = TimedSessionResult["result"]["exerciseResults"][number];

// What one session's rows of one track carry. A bodyweight set leaves no
// top set and no e1RM on the stored result (sessionResult.ts keeps only the
// weighted ones), so a bodyweight exercise carries volume alone, and only
// when the profile has a bodyweight. Its reps are not on the row at all.
// Two rows of one track in one session (legacy rows): the top set is the
// one with the best e1RM, else the heaviest; the volume sums.
function sessionValues(rows: readonly ExerciseRow[]): TrackerSession["values"] {
  let top: ExerciseRow | undefined;
  for (const r of rows) {
    const better =
      top === undefined ||
      (r.topE1rmKg ?? 0) > (top.topE1rmKg ?? 0) ||
      ((r.topE1rmKg ?? 0) === (top.topE1rmKg ?? 0) && (r.topSet?.weightKg ?? 0) > (top.topSet?.weightKg ?? 0));
    if (better) top = r;
  }
  const volume = rows.reduce((sum, r) => sum + r.totalVolumeKg, 0);
  const values: TrackerSession["values"] = {};
  // A zero is no observation: plotted, it would draw a collapse.
  if (top && hasE1rm(top.topE1rmKg)) values.e1rm = top.topE1rmKg;
  if (top?.topSet?.weightKg) values.topWeight = top.topSet.weightKg;
  if (top?.topSet?.reps) values.topReps = top.topSet.reps;
  if (volume > 0) values.volume = volume;
  return values;
}

const inRange = (atMs: number, range: TimeRange) => atMs >= range.fromMs && atMs <= range.toMs;

/** The track of `slug` with the most sessions in `range` (ties: the most
 *  recent), every statistic read off its rows only. No session: an empty
 *  Default track. */
export function trackerTrack(
  results: readonly TimedSessionResult[],
  slug: string,
  range: TimeRange,
): TrackerTrack {
  const tracks = new Map<string, { instance?: string; sessions: TrackerSession[]; lastMs: number }>();
  for (const ts of results) {
    if (!inRange(ts.startedAtMs, range)) continue;
    const byTrack = new Map<string, ExerciseRow[]>();
    for (const r of ts.result.exerciseResults) {
      if (r.exerciseSlug !== slug) continue;
      const key = loadTrackKey(slug, r.equipmentInstance);
      byTrack.set(key, [...(byTrack.get(key) ?? []), r]);
    }
    for (const [key, rows] of byTrack) {
      const values = sessionValues(rows);
      if (Object.keys(values).length === 0) continue;
      const cur = tracks.get(key) ?? {
        ...(rows[0]?.equipmentInstance ? { instance: rows[0].equipmentInstance } : {}),
        sessions: [],
        lastMs: 0,
      };
      cur.sessions.push({ atMs: ts.startedAtMs, values });
      cur.lastMs = Math.max(cur.lastMs, ts.startedAtMs);
      tracks.set(key, cur);
    }
  }
  let best: { instance?: string; sessions: TrackerSession[]; lastMs: number } | undefined;
  for (const t of tracks.values()) {
    if (
      best === undefined ||
      t.sessions.length > best.sessions.length ||
      (t.sessions.length === best.sessions.length && t.lastMs > best.lastMs)
    ) {
      best = t;
    }
  }
  return {
    ...(best?.instance ? { equipmentInstance: best.instance } : {}),
    sessions: (best?.sessions ?? []).sort((a, b) => a.atMs - b.atMs),
    trackCount: tracks.size,
  };
}

/** The exercises with something to track in `range`, most logged first: by
 *  sets logged, ties to the more recent. The first is the widget's default. */
export function trackableSlugs(results: readonly TimedSessionResult[], range: TimeRange): string[] {
  const seen = new Map<string, { sets: number; lastMs: number }>();
  for (const ts of results) {
    if (!inRange(ts.startedAtMs, range)) continue;
    for (const r of ts.result.exerciseResults) {
      if (Object.keys(sessionValues([r])).length === 0) continue;
      const cur = seen.get(r.exerciseSlug) ?? { sets: 0, lastMs: 0 };
      cur.sets += r.setCount;
      cur.lastMs = Math.max(cur.lastMs, ts.startedAtMs);
      seen.set(r.exerciseSlug, cur);
    }
  }
  return [...seen.entries()]
    .sort(([, a], [, b]) => b.sets - a.sets || b.lastMs - a.lastMs)
    .map(([slug]) => slug);
}
